import { inject, Injectable } from '@angular/core';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import {
  EMPTY,
  filter,
  interval,
  Observable,
  ReplaySubject,
  switchMap,
  take,
} from 'rxjs';

import { LaunchDarklyFlags } from '@models/feature-flag.enum';
import { EnvironmentService } from './environment.service';
import { HostType } from '@models/host-type.enum';
import { AuthData } from '@interfaces/auth-data';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private client!: LaunchDarkly.LDClient;
  private activeListeners: Partial<
    Record<LaunchDarklyFlags, ReplaySubject<boolean>>
  > = {};
  private environmentService = inject(EnvironmentService);

  initializeLaunchDarkly(profile?: AuthData): void {
    if (!profile) {
      console.log('No profile found, skipping LaunchDarkly initialization');
      return;
    }

    const host = this.environmentService.getHostType();
    let LAUNCH_DARKLY_CLIENT_ID: string;
    switch (host) {
      case HostType.DEV:
      case HostType.LOCAL:
        LAUNCH_DARKLY_CLIENT_ID = '676195b671eaf00b75c69e88';
        break;

      case HostType.QA:
        LAUNCH_DARKLY_CLIENT_ID = '676195a7b6863b0974448b83';
        break;

      case HostType.RT:
        LAUNCH_DARKLY_CLIENT_ID = '67619589a6712f0952f469fe';
        break;

      case HostType.PROD:
        LAUNCH_DARKLY_CLIENT_ID = '67619555ff094e0b7a0767b9';
        break;

      default:
        LAUNCH_DARKLY_CLIENT_ID = '676195b671eaf00b75c69e88';
        break;
    }
    const user: LaunchDarkly.LDContext = {
      kind: 'user',
      name: `${profile.firstname} ${profile.lastname}`,
      key: profile.id,
    };

    const account: LaunchDarkly.LDContext = {
      kind: 'account',
      name: profile.username,
      key: `${profile.accountid}`,
    };

    const multiContext = {
      kind: 'multi',
      user,
      account,
    };

    this.client = LaunchDarkly.initialize(
      LAUNCH_DARKLY_CLIENT_ID,
      multiContext,
    );
  }

  /**
   * To get the LD flag and it's current status
   * @param flagKey key of the key. Can be found in Launch Darkly UI in corresponding flag, should be defined in enum
   * @param defaultValue  - expected to be false for any feature.
   * @returns The state of the flag OR void if LD is not initialized.
   * @NOTE If the flag is not available in current environment - will be returned as false
   */
  getFlag(
    flagKey: LaunchDarklyFlags,
    defaultValue: LaunchDarkly.LDFlagValue = false,
  ): Observable<LaunchDarkly.LDFlagValue> {
    if (!this.client) {
      return this.waitForClient().pipe(
        switchMap(() => {
          return this.getFlag(flagKey, defaultValue);
        }),
      );
    }

    if (this.activeListeners[flagKey]) {
      return this.activeListeners[flagKey].asObservable();
    }

    this.activeListeners[flagKey] = new ReplaySubject<boolean>(1);
    const flagListener = this.activeListeners[flagKey];

    if (this.client) {
      this.client.waitUntilReady().then(() => {
        const flagValue = this.client.variation(
          flagKey,
          defaultValue,
        ) as LaunchDarkly.LDFlagValue;
        flagListener.next(flagValue);
      });

      // catch the changes of flag
      this.client.on(`change:${flagKey}`, (value: LaunchDarkly.LDFlagValue) => {
        flagListener.next(value);
      });

      return flagListener;
    }

    return this.activeListeners[flagKey].asObservable();
  }

  waitForClient(): Observable<number> {
    if (this.client) {
      return EMPTY;
    }

    return interval(100).pipe(
      filter(() => Boolean(this.client)),
      take(1),
    );
  }
}
